import {toast} from '@Toast';
import axios, {AxiosRequestConfig, InternalAxiosRequestConfig} from 'axios';
import {userOnlyPaths} from 'routes/ProtectedRoute';
import {routes} from 'routes/routes';

export type CustomAxiosConfig = AxiosRequestConfig & {
  goBaseUrl?: boolean;
  winBaseURL?: boolean;
  vendorHubBaseUrl?: boolean;
  _retry?: boolean;
  baseUrl?: boolean;
};

let baseURL = '';

const publicPaths = [routes.LOGIN, routes.USER_REGISTER];

const redirectToAuthPage = (path: string) => {
  if (publicPaths.includes(window.location.pathname)) {
    return;
  }
  window.location.href = path;
};

const requestOnFullFilled = (config: InternalAxiosRequestConfig) => {
  const customConfig = config as InternalAxiosRequestConfig & CustomAxiosConfig;
  if (customConfig.url?.includes('kafka-events')) {
    baseURL = import.meta.env.VITE_CORE_URL || '';
  } else if (import.meta.env.VITE_BASE_URL && customConfig.baseUrl) {
    baseURL = import.meta.env.VITE_BASE_URL || '';
  } else if (
    import.meta.env.VITE_AUTH_URL &&
    !customConfig.winBaseURL &&
    !customConfig.goBaseUrl &&
    !customConfig.vendorHubBaseUrl
  ) {
    baseURL = import.meta.env.VITE_VENDOR_URL || '';
  } else if (
    import.meta.env.VITE_GO_SERVICE_URL &&
    customConfig.goBaseUrl &&
    !customConfig.winBaseURL
  ) {
    baseURL = import.meta.env.VITE_GO_SERVICE_URL || '';
  } else if (import.meta.env.VITE_WIN_VENDOR_URL && customConfig.winBaseURL) {
    baseURL = import.meta.env.VITE_WIN_VENDOR_URL || '';
  } else if (import.meta.env.VITE_VENDOR_HUB_URL && customConfig.vendorHubBaseUrl) {
    baseURL = import.meta.env.VITE_VENDOR_HUB_URL || '';
  }

  const token = localStorage.getItem('access_token');

  if (token) customConfig.headers.Authorization = `Bearer ${token}`;

  customConfig.baseURL = baseURL;

  return customConfig;
};

const requestOnRejected = (error: any) => Promise.reject(error);

const responseOnRejected = async (error: any) => {
  if (import.meta.env.VITE_BASE_URL) {
    baseURL = import.meta.env.VITE_BASE_URL || '';
  }

  if (error.message === 'Network Error' && !error.response) {
    throw error;
  }
  const {status, data} = error?.response || {};

  if (data?.message && status !== 400) {
    return error?.response;
  }

  const originalRequest = error.config;
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      const res = await axios.post(`${baseURL}auth/dakhl/refresh`, {refresh_token: refreshToken});
      if (res.status === 200) {
        const newAccessToken = res.data.access_token;
        const newRefreshToken = res.data.refresh_token;

        localStorage.setItem('access_token', newAccessToken);
        localStorage.setItem('refresh_token', newRefreshToken);

        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAccessToken}`
        };
        return axios(originalRequest);
      } else {
        toast('توکن منقضی شده است', 'rgba(211, 1, 10, 0.85)', 2000);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (userOnlyPaths.includes(window.location.pathname)) {
          redirectToAuthPage(
            `/user-register?redirect=${encodeURIComponent(window.location.pathname)}`
          );
        } else {
          redirectToAuthPage(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
        }
      }
    } else {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      if (userOnlyPaths.includes(window.location.pathname)) {
        redirectToAuthPage(
          `/user-register?redirect=${encodeURIComponent(window.location.pathname)}`
        );
      } else {
        redirectToAuthPage('/login');
      }
    }
  }
  if (status === 404) {
    toast('اطلاعات درخواستی یافت نشد', 'rgba(211, 1, 10, 0.85)', 2000);
    return error?.response;
  } else if (status === 400) {
    toast(data?.errors?.message || data?.error?.message || data?.message);
  }

  if (status === 500) {
    toast('خطایی در سرور رخ داده است', 'rgba(211, 1, 10, 0.85)');
    throw error?.response;
  }

  throw error?.response;
};

axios.interceptors.request.use(requestOnFullFilled, requestOnRejected);
axios.interceptors.response.use(undefined, responseOnRejected);

export default axios;
